import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type CriteriaFormItem = {
  maxGrade?: number;
};

export type UseAssessmentCriteriaValidateProps = {
  criteria: CriteriaFormItem[];
  itemMaxGrade: number;
};

export type ValidateCriteriaFn = (
  next: CriteriaFormItem[] | undefined,
) => true | string;

const sumGrades = (list: CriteriaFormItem[]) =>
  list.reduce((total, c) => total + (Number(c?.maxGrade) || 0), 0);

const useAssessmentCriteriaValidate = ({
  criteria,
  itemMaxGrade,
}: UseAssessmentCriteriaValidateProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const getMaxAvailable = useCallback(
    (index: number) =>
      Math.max(itemMaxGrade - sumGrades(criteria.slice(0, index)), 0),
    [criteria, itemMaxGrade],
  );

  const validateCriteria = useCallback<ValidateCriteriaFn>(
    next => {
      if (!next || next.length === 0) return true;

      const sum = sumGrades(next);

      if (sum > itemMaxGrade) {
        return t('criteriaSumMismatch', {
          expected: itemMaxGrade,
          actual: sum,
        });
      }

      return true;
    },
    [itemMaxGrade, t],
  );

  return { validateCriteria, getMaxAvailable };
};

export default useAssessmentCriteriaValidate;
